import { GridRowParams } from '@material-ui/data-grid';
import React, { useState, useContext } from 'react';
import { toast } from 'react-toastify';

import FormDefault from '~/components/FormDefault';
import Search from '~/components/Search';

import { Container } from './styles';

import { Table, Form } from './components';
import { useUsuario } from './UsuarioContext';
import { validaEmail } from '~/utils/functions';
import { Loja, RequestCreateUser, SubmitFormProps } from './protocols';
import DefaultLoader from '~/components/DefaultLoader';
import apiUsuario from './services';
import { LojaContext } from '~/context/loja';
import useAuth from '~/hooks/useAuth';
import { getUserData } from '~/services/user';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

export const UsuarioContent: React.FC = () => {
  const [showSearch, setShowSearch] = useState(true);
  const { changeLoja, codLoja } = useContext(LojaContext);
  const user = useAuth();
  const { flg_superadmin } = getUserData();

  const {
    loader,
    isUpdate,
    handleChangeIsUpdate,
    formUsuario,
    handleClearFormUsuario,
    getUsuario,
    handleFormDisabled,
    sendEmailValidateUser,
    codUsuario,
    setCodUsuario,
    handleSubmit,
    setValue,
    getValues,
    validPass,
    handleValidPass,
    setError,
    handleClicouGravar,
  } = useUsuario();

  const email = user?.user?.email?.toString().trim().toLowerCase() ?? '';
  const isEmailKonvix =
    email.endsWith('@konvix.com.br') || email.endsWith('@konvix.com');

  const showDeleteButton =
    Boolean(isUpdate) && flg_superadmin === true && isEmailKonvix;

  const prepareBodyRequest = (data: SubmitFormProps): RequestCreateUser => {
    const requestBody: RequestCreateUser = {
      des_usuario: data.des_usuario,
      email: String(data.email).toLowerCase(),
      password: data.password,
      timezone: data.timezone.value,
      cod_controle: data.cod_controle.value,
      lojas: data.lojas,
      flg_inativo: data.flg_inativo,
      flg_admin: data.flg_admin,
      flg_superadmin: data.flg_superadmin,
    };
    return requestBody;
  };

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param;

    setCodUsuario(row.cod_usuario);
    setValue('cod_usuario', row.cod_usuario);
    getUsuario(row.cod_usuario);

    handleFormDisabled(false);
    setShowSearch(false);
    handleChangeIsUpdate(true);
  };

  const onCancel = () => {
    handleClearFormUsuario();
    handleChangeIsUpdate(false);
    setShowSearch(true);
  };

  const onDelete = async () => {
    const { cod_usuario } = getValues();

    const nome = (getValues('des_usuario') as string) ?? '';
    const emailSelecionado = (getValues('email') as string) ?? '';

    const result = await MySwal.fire({
      title: 'Remover usuário?',
      html: (
        <div style={{ textAlign: 'left' }}>
          <p>
            Você está prestes a remover{' '}
            {nome ? <strong>{nome}</strong> : 'este usuário'}
            {emailSelecionado ? (
              <>
                {' '}
                (<strong>{emailSelecionado}</strong>)
              </>
            ) : null}
            .
          </p>

          <p style={{ marginTop: 8 }}>
            Essa ação remove o cadastro do usuário do sistema.
          </p>

          {formUsuario.flg_inativo.value ? (
            <p style={{ marginTop: 8 }}>
              <strong>Atenção:</strong> este usuário já está marcado como{' '}
              <strong>Inativo</strong>.
            </p>
          ) : null}
        </div>
      ),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Não, cancelar',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    const { success, message } = await apiUsuario.delete(cod_usuario, codLoja);

    if (!success) {
      toast.warning(message);
      return;
    }

    toast.success(message);
    onCancel();
  };

  const onNew = () => {
    handleClearFormUsuario();
    handleChangeIsUpdate(false);
    setShowSearch(false);
  };

  const onSave = handleSubmit(async (data: SubmitFormProps) => {
    const emailIsValid = validaEmail(data.email);
    if (!emailIsValid) return toast.warn('O Email informado é inválido!');

    if (!data.lojas && !formUsuario.flg_inativo.value)
      return toast.warn('É necessário vincular ao menos uma loja ao usuário.');

    const hasPadrao = data.lojas.find((loja: Loja) => {
      return loja.flg_padrao;
    });

    if (!hasPadrao && !formUsuario.flg_inativo.value)
      return toast.warn('Defina uma loja padrão para o usuário.');

    const requestBody: RequestCreateUser = prepareBodyRequest(data);

    if (!isUpdate) {
      await apiUsuario.create(requestBody);

      const sendMail = await sendEmailValidateUser();
      handleClearFormUsuario();

      if (!sendMail.success) toast.warning(sendMail.message);

      return toast.success(
        <span>
          Usuário cadastrado com sucesso <br /> {sendMail.message}
        </span>,
      );
    }

    const cod_usuario = getValues('cod_usuario');
    const resultRequest = await apiUsuario.update(requestBody, cod_usuario);

    const { success, message } = resultRequest;

    if (!success) throw new Error(message);

    if (user.user.id === codUsuario) {
      if (requestBody.lojas) {
        const lojaPadrao = requestBody.lojas.find((loj: any) => loj.flg_padrao);

        if (lojaPadrao) {
          changeLoja(lojaPadrao.cod_loja);
        }
      }
    }

    onCancel();
    handleClicouGravar(false);
    return toast.success(message);
  });

  if (loader) {
    return <DefaultLoader />;
  }

  return (
    <Container>
      {showSearch && (
        <Search codTela={2} newData={onNew} onRowClick={onRowClick} />
      )}
      {!showSearch && (
        <FormDefault
          title="Cadastro de Usuário"
          codigoRegistro={[{ value: codUsuario, des_campo: 'Código' }]}
          codTela={2}
          onSave={async () => {
            handleClicouGravar(true);

            if (!isUpdate && getValues('password').length <= 0) {
              handleValidPass(false);
            }

            if (!validPass && getValues('password').length > 0) {
              toast.warning('Verifique se sua senha atende aos requisitos');
              setError('password', { type: 'required' });
              return;
            }
            await onSave();
          }}
          onCancel={onCancel}
          isUpdate={isUpdate}
          onNew={onNew}
          onDelete={onDelete}
          onClearFields={handleClearFormUsuario}
          onReturnSearch={onCancel}
          labelButtonTrash="Remover usuário"
          isDeactivateDeleteMessage
          isDelete={showDeleteButton}
        >
          <Form />
          <Table />
        </FormDefault>
      )}
    </Container>
  );
};
